import React, { useState, useEffect, useReducer, useMemo } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: theme.palette.text.primary,
		zIndex: 2,
		backgroundColor: theme.palette.background.paper,
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: theme.palette.text.secondary,
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const getActivityTime = ticket => {
	const parsed = new Date(ticket?.updatedAt || ticket?.createdAt || 0).getTime();
	return Number.isFinite(parsed) ? parsed : 0;
};

const sortByLatestActivity = tickets =>
	[...tickets].sort((left, right) => {
		const timeDiff = getActivityTime(right) - getActivityTime(left);
		if (timeDiff !== 0) {
			return timeDiff;
		}
		return Number(right?.id || 0) - Number(left?.id || 0);
	});

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;
		let nextState = [...state];

		newTickets.forEach(ticket => {
			const ticketIndex = nextState.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				nextState[ticketIndex] = {
					...nextState[ticketIndex],
					...ticket
				};
			} else {
				nextState = [...nextState, ticket];
			}
		});

		return sortByLatestActivity(nextState);
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		return state.map(ticket =>
			ticket.id === ticketId
				? {
						...ticket,
						unreadMessages: 0
				  }
				: ticket
		);
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			const nextState = [...state];
			nextState[ticketIndex] = {
				...nextState[ticketIndex],
				...ticket
			};
			return sortByLatestActivity(nextState);
		}

		return sortByLatestActivity([...state, ticket]);
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			const nextState = [...state];
			nextState[ticketIndex] = {
				...nextState[ticketIndex],
				...ticket
			};
			return sortByLatestActivity(nextState);
		}

		return sortByLatestActivity([...state, ticket]);
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		return state.map(ticket =>
			ticket.contactId === contact.id
				? {
						...ticket,
						contact
				  }
				: ticket
		);
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		return state.filter(ticket => ticket.id !== ticketId);
	}

	if (action.type === "RESET") {
		return [];
	}

	return state;
};

const normalizeStatusFilters = status =>
	(status || "")
		.split(",")
		.map(value => value.trim())
		.filter(Boolean);

	const TicketsList = (props) => {
		const { status, searchParam, showAll, selectedQueueIds, updateCount, style } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const statusFilters = useMemo(() => normalizeStatusFilters(status), [status]);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		refreshTrigger,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();
		const joinScope = () => {
			if (statusFilters.length > 0) {
				statusFilters.forEach(ticketStatus => socket.emit("joinTickets", ticketStatus));
			} else {
				socket.emit("joinNotification");
			}
		};
		const leaveScope = () => {
			if (statusFilters.length > 0) {
				statusFilters.forEach(ticketStatus => socket.emit("leaveTickets", ticketStatus));
			} else {
				socket.emit("leaveNotification");
			}
		};

		const matchesCurrentStatus = ticket =>
			statusFilters.length === 0 || statusFilters.includes(ticket.status);

		const hasQueueFilter = Array.isArray(selectedQueueIds) && selectedQueueIds.length > 0;

		const shouldUpdateTicket = ticket =>
			!searchParam &&
			matchesCurrentStatus(ticket) &&
			(!hasQueueFilter ||
				!ticket.queueId ||
				selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToUserQueues = ticket =>
			hasQueueFilter &&
			ticket.queueId &&
			selectedQueueIds.indexOf(ticket.queueId) === -1;

		const handleConnect = () => {
			joinScope();
			setRefreshTrigger(prevState => prevState + 1);
		};

		socket.on("connect", handleConnect);
		joinScope();

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			const ticketPayload = data?.ticket;

			if (data.action === "update" && ticketPayload && statusFilters.length > 0 && !matchesCurrentStatus(ticketPayload)) {
				dispatch({ type: "DELETE_TICKET", payload: ticketPayload.id });
			}

			if (data.action === "update" && ticketPayload && shouldUpdateTicket(ticketPayload)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: ticketPayload,
				});
			}

			if (data.action === "update" && ticketPayload && notBelongsToUserQueues(ticketPayload)) {
				dispatch({ type: "DELETE_TICKET", payload: ticketPayload.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			const ticketPayload = data?.ticket;

			if (!ticketPayload) {
				return;
			}

			if (data.action === "create" && statusFilters.length > 0 && !matchesCurrentStatus(ticketPayload)) {
				dispatch({ type: "DELETE_TICKET", payload: ticketPayload.id });
			}

			if (data.action === "create" && shouldUpdateTicket(ticketPayload)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: ticketPayload,
				});
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.off("connect", handleConnect);
			leaveScope();
			socket.disconnect();
		};
	}, [searchParam, showAll, selectedQueueIds, statusFilters]);

	useEffect(() => {
		const triggerRefresh = () => {
			setRefreshTrigger(prevState => prevState + 1);
		};

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				triggerRefresh();
			}
		};

		const handleFocus = () => {
			triggerRefresh();
		};

		const handleOnline = () => {
			triggerRefresh();
		};

		const refreshInterval = setInterval(() => {
			if (!document.hidden) {
				triggerRefresh();
			}
		}, 7000);

		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("focus", handleFocus);
		window.addEventListener("online", handleOnline);

		return () => {
			clearInterval(refreshInterval);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("online", handleOnline);
		};
	}, []);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map(ticket => (
								<TicketListItem ticket={ticket} key={ticket.id} />
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
